import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { GhostConnectorIcons } from './GhostConnectorIcons.tsx';
import type { ConnectableApp } from './hooks/useConnectableApps.ts';

export const HIGH_ADOPTION_THRESHOLD = 30;

const i18n = defineMessages({
	gdriveMessage: {
		id: 'byline-connections.preview-panel.modal.promo-banner.gdrive',
		defaultMessage: 'Connect Google Drive to get rich previews and more powerful search',
		description:
			'Promotional message in the Teamwork Graph modal prompting the user to connect their Google Drive account, shown when their organization already links to Google Drive but they have not connected it themselves.',
	},
	highAdoptionMessage: {
		id: 'byline-connections.preview-panel.modal.promo-banner.high-adoption',
		defaultMessage:
			// oxlint-disable-next-line eslint-core/no-restricted-syntax
			"{percent}% of your org uses connected apps to see how their pages connect to their team's work",
		description:
			'Promotional message in the Teamwork Graph modal showing the percentage of the organization that uses connected apps to see how their pages connect to their team work.',
	},
	genericMessage: {
		id: 'byline-connections.preview-panel.modal.promo-banner.generic',
		defaultMessage: 'Top teams use connected apps to get rich previews and more powerful search',
		description:
			'Generic promotional message shown in the Teamwork Graph modal when no connected app has high enough adoption in the organization to name a percentage.',
	},
	connectApps: {
		id: 'byline-connections.preview-panel.modal.promo-banner.connect-apps',
		defaultMessage: 'Connect apps',
		description:
			'Accessible name of the promotional banner in the Teamwork Graph modal, which is a button that opens the app connection dialog.',
	},
});

/** Eyeballed from the mock — confirm against Figma. */
const GRID_PITCH = '32px';

/** Negative moves the lines up. Wraps every `GRID_PITCH`, so stay under it. */
const GRID_OFFSET_BLOCK = '-16px';

const styles = cssMap({
	banner: {
		display: 'block',
		width: '100%',
		boxSizing: 'border-box',
		textAlign: 'start',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.large'),
		borderStyle: 'none',
		backgroundColor: token('elevation.surface.sunken'),
		cursor: 'pointer',
		// Clips the grid to the corner radius.
		overflow: 'hidden',
		position: 'relative',
		transition: token('motion.button.hovered'),
		// `elevation.surface.sunken` has no `.hovered` pair and this slot only accepts
		// `*.hovered` tokens; this one composites to the same `#F0F1F2`.
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	/*
	 * A scrim of the card colour over crossed lines, fading the grid out across the inline
	 * axis. The design fades it this way too; `mask-image` would be the obvious tool but
	 * needs a vendor-prefixed fallback, which `cssMap` rejects.
	 */
	grid: {
		position: 'absolute',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		backgroundImage: `linear-gradient(to right, ${token('elevation.surface.sunken')}, transparent), linear-gradient(90deg, ${token('color.border')} 1px, transparent 1px), linear-gradient(0deg, ${token('color.border')} 1px, transparent 1px)`,
		backgroundSize: `100% 100%, ${GRID_PITCH} ${GRID_PITCH}, ${GRID_PITCH} ${GRID_PITCH}`,
		// Only the third layer, the horizontal lines, takes the offset.
		backgroundPosition: `0 0, 0 0, 0 ${GRID_OFFSET_BLOCK}`,
		pointerEvents: 'none',
	},
	// The absolutely positioned grid paints over in-flow siblings without this.
	content: {
		position: 'relative',
	},
	message: {
		font: token('font.body.small'),
		color: token('color.text'),
	},
});

export type ConnectorPromoBannerVariant = 'gdrive' | 'orgConnectedApps' | 'generic';

export type ConnectorPromoBannerProps = {
	variant: ConnectorPromoBannerVariant;
	/** Only read for the `orgConnectedApps` variant. */
	topAppPercent?: number;
	apps: ConnectableApp[];
	onClick: () => void;
};

const getMessageDescriptor = (variant: ConnectorPromoBannerVariant, topAppPercent?: number) => {
	if (variant === 'gdrive') {
		return i18n.gdriveMessage;
	}
	// Below the threshold the org copy is the generic copy, so the number is only quoted
	// when it is worth quoting.
	if (
		variant === 'orgConnectedApps' &&
		topAppPercent !== undefined &&
		topAppPercent >= HIGH_ADOPTION_THRESHOLD
	) {
		return i18n.highAdoptionMessage;
	}
	return i18n.genericMessage;
};

/**
 * Deliberately not dismissible, unlike the byline menu footer banners: we want to see how
 * it performs before deciding whether users can hide it, so there is no dismissal state.
 */
export const ConnectorPromoBanner = ({
	variant,
	topAppPercent,
	apps,
	onClick,
}: ConnectorPromoBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Pressable
			xcss={styles.banner}
			onClick={onClick}
			aria-label={formatMessage(i18n.connectApps)}
			testId="teamwork-graph-promo-banner"
		>
			<Box xcss={styles.grid} aria-hidden="true" />
			<Stack space="space.150" xcss={styles.content}>
				<Inline space="space.100" alignBlock="center" spread="space-between">
					<GhostConnectorIcons apps={apps} />
					<ArrowRightIcon label="" spacing="none" color={token('color.icon.subtle')} />
				</Inline>
				<Box xcss={styles.message}>
					{formatMessage(getMessageDescriptor(variant, topAppPercent), {
						percent: topAppPercent,
					})}
				</Box>
			</Stack>
		</Pressable>
	);
};
